/**
 * QUÉ SE LE DICE A LA PERSONA CUANDO ALGO FALLA — EN UN SOLO ARCHIVO.
 *
 * ── DE DÓNDE SALE (2026-08-31) ──────────────────────────────────────────────
 * Lo pidió la usuaria el 2026-08-31, con un caso delante: el asesor 518 llevaba
 * media acta de devolución escrita y el portal le contestaba «No autenticado».
 * Su sesión había caducado a las 05:19 de esa mañana (entró a las 21:19 del día
 * anterior y el token dura 8 h fijas), pero el mensaje no lo decía y el portal
 * web tampoco lo devolvía al acceso: se quedaba dándole a Enviar contra una
 * cookie que el navegador ya había borrado.
 *
 * «Necesito que el error diga exactamente qué pasó»: si caducó la sesión, que
 * diga que caducó y que vuelva a entrar; si es la conexión, que diga que es la
 * conexión. Los textos de abajo son esa lista, y son SUYOS.
 *
 * ── POR QUÉ AQUÍ Y NO EN CADA PANTALLA ──────────────────────────────────────
 * Lo consumen LOS TRES lados, igual que `clave.ts`:
 *  · el servidor (`netlify/functions/_lib/auth.ts`, con `../../../src/lib/…js`),
 *    que es quien pone el CÓDIGO en la respuesta;
 *  · el portal web (`src/lib/api.ts`), que traduce el código a este texto una
 *    sola vez, en el cliente HTTP, para las 76 pantallas de golpe;
 *  · la app del teléfono (`public/movil/api.js`), que es JavaScript servido tal
 *    cual y NO PUEDE IMPORTAR de aquí: repite los textos a mano con un
 *    comentario que apunta a este archivo. Si se cambia una frase aquí, se
 *    cambia allí — y `errores.test.ts` lo comprueba leyendo los dos archivos.
 *
 * ── LA REGLA DE FONDO ───────────────────────────────────────────────────────
 * Se decide por el CÓDIGO y por el status, NUNCA por el texto del servidor: ese
 * texto está escrito para una persona y se reescribe sin saber que algo lo
 * estaba comparando. Y donde el servidor SÍ tiene algo que decir que el cliente
 * no puede saber —«Falta el código del cliente», «El monto supera tu bolsa»—,
 * su texto MANDA: aquí solo se reemplazan los mensajes técnicos que no le dicen
 * nada a quien está en la calle con el teléfono en la mano.
 */

/** No llegó cookie: la sesión nunca se abrió, o el navegador ya la borró. */
export const CODIGO_SESION_AUSENTE = 'SESION_AUSENTE';
/** Llegó cookie pero el token no vale: caducado (8 h) o firmado con otra clave. */
export const CODIGO_SESION_EXPIRADA = 'SESION_EXPIRADA';

/**
 * Los dos 401 que significan «vuelve a entrar». Para la persona son el mismo
 * hecho y el mismo remedio, así que dan el mismo mensaje; se distinguen en la
 * respuesta porque en un diagnóstico NO es lo mismo «no mandó cookie» que
 * «mandó una cookie que no vale», y esa diferencia es la que resolvió el caso
 * del asesor 518.
 *
 * ⚠️ Los OTROS 401 del portal NO están aquí y no deben estarlo: el de
 * `auth-login.mts` («Usuario o contraseña incorrectos») y el de
 * `auth-password.mts` («La clave actual no es correcta»). Los dos ocurren
 * mientras la persona está tecleando su clave, y decirle «sesión caducada»
 * ahí sería mentirle. Por eso esto va por código y no por status.
 */
export const CODIGOS_DE_SESION: readonly string[] = [
  CODIGO_SESION_AUSENTE,
  CODIGO_SESION_EXPIRADA,
];

export const esFalloDeSesion = (status: number, codigo?: string | null): boolean =>
  status === 401 && !!codigo && CODIGOS_DE_SESION.includes(codigo);

/**
 * LOS TEXTOS. Con la forma que pidió la usuaria: primero QUÉ pasó, después QUÉ
 * hacer. Sin jerga y sin números de status dentro de la frase — el número se
 * añade aparte donde sirve para pedir ayuda (los 500), porque a quien reporta
 * el problema le hace falta y a quien solo trabaja no le estorba al final.
 */
export const MENSAJES = {
  /** El caso del asesor 518. Es la frase de la usuaria, palabra por palabra. */
  sesion: 'Sesión caducada. Por favor, ingrese nuevamente con su usuario y contraseña.',
  /** 2026-10-05 · El cierre AUTOMÁTICO a las 8 h: no es un error, es el aviso de por qué está en el acceso. */
  sesionCerrada: 'Su sesión de 8 horas terminó y se cerró automáticamente. Ingrese nuevamente con su usuario y contraseña.',
  /** El teléfono se quedó sin red: `navigator.onLine` dice que no hay. */
  sinInternet: 'Error de conexión a internet. Su equipo no tiene red: revise el wifi o los datos móviles e intente de nuevo.',
  /** Hay red, pero la petición no llegó o no volvió. En la práctica es la señal del teléfono: ASESOR510
   *  lo vio el 23/10 y el 03/11 con el portal atendiendo a los demás, y la usuaria pidió (03/11) que
   *  diga que es el internet inestable en vez de «el servidor no respondió». */
  sinPortal: 'Error de conexión: su internet está inestable y la solicitud no llegó al portal. Espere unos segundos e intente de nuevo; si sigue igual, cambie del wifi a los datos móviles (o al revés).',
  permiso: 'Su usuario no tiene permiso para esta sección. Si cree que debería tenerlo, escriba a Control Estadístico.',
  noExiste: 'Eso ya no existe en el portal. Vuelva a entrar por el menú; si llegó por un enlace guardado, ese enlace quedó viejo.',
  lento: 'El portal tardó demasiado en responder. Intente de nuevo; si estaba enviando algo, revise antes si quedó guardado.',
  pesado: 'El envío pesa más de lo que el portal admite de una vez. Quite algún adjunto o mándelo más liviano.',
  muchosIntentos: 'Demasiados intentos seguidos. Espere un minuto e intente de nuevo.',
  datos: 'Hay datos incompletos o inválidos. Revise el formulario e intente de nuevo.',
  servidor: 'Error interno del portal. No es por lo que usted escribió: intente de nuevo en unos minutos y, si sigue igual, avise a Control Estadístico.',
} as const;

export interface OpcionesFallo {
  /** El `codigo` de la respuesta, si trajo. NUNCA se mira el texto. */
  codigo?: string | null | undefined;
  /**
   * `navigator.onLine` en el momento del fallo. Se recibe en vez de leerse
   * aquí porque este módulo lo importa también el SERVIDOR, donde `navigator`
   * no existe. `undefined` = no se sabe, y entonces no se afirma que no hay
   * internet: se dice que no se pudo llegar al portal, que es lo medido.
   */
  enLinea?: boolean | undefined;
}

/**
 * El texto que ve la persona, a partir de lo único que se sabe de verdad: el
 * status HTTP, el código de la respuesta y si el equipo tenía red.
 *
 * `status === 0` es la convención de los dos clientes para «`fetch` lanzó»: la
 * petición ni siquiera llegó a tener respuesta.
 */
export function mensajeDeFallo(
  status: number,
  textoServidor?: string | null,
  op: OpcionesFallo = {},
): string {
  const delServidor = (textoServidor ?? '').trim();

  if (status === 0) {
    return op.enLinea === false ? MENSAJES.sinInternet : MENSAJES.sinPortal;
  }
  if (esFalloDeSesion(status, op.codigo)) return MENSAJES.sesion;

  switch (status) {
    /* El portón de contraseña es el único 403 con código, y su texto lo escribe
     * el servidor para esa pantalla concreta: manda él. */
    case 403: return delServidor && op.codigo ? delServidor : MENSAJES.permiso;
    case 404: return MENSAJES.noExiste;
    case 408:
    case 504: return MENSAJES.lento;
    case 413: return MENSAJES.pesado;
    case 429: return delServidor || MENSAJES.muchosIntentos;
    default:
      /* Los 5xx del portal contestan «Error interno» y ya: ese texto no le dice
       * nada a nadie, así que se reemplaza — y se conserva el número, que es lo
       * único que sirve para buscarlo en el registro. */
      if (status >= 500) return `${MENSAJES.servidor} (Error ${status}.)`;
      /* 400, 409, 422… — aquí el servidor SÍ sabe qué falta y lo dice con
       * nombre y apellido. Su texto manda; el genérico es solo el respaldo. */
      return delServidor || `${MENSAJES.datos} (Error ${status}.)`;
  }
}

/* ─────────────────────────────────────────────────────────────────────────────
   EL AVISO DE CADUCIDAD, para que alguien devuelva al acceso.

   Vive aquí y no en `api.ts` por una razón concreta: `api.ts` NO es el único que
   llama al portal. Las descargas binarias —actas en PDF, libros de Excel,
   adjuntos por piezas— hacen su propio `fetch` porque necesitan el `blob`, y
   `subida-partes.ts` además tiene prohibido importar de `api.ts` (sería un
   ciclo: `api.ts` importa de él). Este módulo es hoja de todos, así que el
   gancho puesto aquí lo alcanzan todos.

   Lo registra `ProveedorSesion` (`src/lib/sesion.tsx`) y es de UNO en uno: hay
   un solo proveedor de sesión en el portal. En el servidor, que importa este
   archivo por los dos códigos de arriba, nunca se registra nada y esto es una
   variable a `null` que no hace nada.
   ───────────────────────────────────────────────────────────────────────────── */

type AlCaducarSesion = (mensaje: string) => void;
let alCaducar: AlCaducarSesion | null = null;

/** `null` para quitarlo. Lo llama el proveedor de sesión al montarse. */
export function registrarCaducidadDeSesion(fn: AlCaducarSesion | null): void {
  alCaducar = fn;
}

/**
 * Avisa —si toca— de que la sesión murió, y devuelve el mismo mensaje para que
 * quien llame lo ponga también en su error. Se llama SIEMPRE antes de lanzar:
 * quien pidió el dato puede tragarse el error, y la sesión hay que cerrarla
 * igual.
 */
export function avisarSiCaducoLaSesion(
  status: number,
  codigo: string | null | undefined,
  mensaje: string,
): void {
  if (esFalloDeSesion(status, codigo) && alCaducar) alCaducar(mensaje);
}

/* ═════════════════════════════════════════════════════════════════════════════
   LA ACCIÓN QUE NO SE COMPLETÓ · ¿QUEDÓ EL DATO O NO QUEDÓ?
   ═════════════════════════════════════════════════════════════════════════════

   ── DE DÓNDE SALE (2026-09-03) ─────────────────────────────────────────────
   La usuaria: «varios usuarios realizan alguna acción —cargar una negociación,
   una planificación, una devolución— y luego no queda cargada y tampoco les
   sale ningún mensaje de error». Lo que se midió en el código del portal WEB es
   que el mensaje casi nunca falta: se calcula bien y se pinta donde la persona
   NO está mirando. Distancia en líneas de JSX entre el botón que dispara y el
   sitio donde se pinta el fallo: 854 en Planificación Coordinadores, 380 en
   Inactivación, 354 en Devoluciones —con cuatro secciones de formulario en
   medio—, 211 en Planificación, 178 en Prospecto, 136 en Prospectos RRSS, 108
   en Negociaciones, 100 en el reporte de Activación/Inactivación. El asesor
   rellena el acta, baja hasta «Enviar solicitud», el rojo aparece 354 líneas
   más arriba fuera de pantalla, el botón vuelve de «Generando ticket…» a
   «Enviar solicitud», y no ve NADA.

   La app del teléfono NO tiene este problema y por eso el reporte llega solo de
   la web: su `mostrarError()` (`public/movil/gestiones.js`) pinta el rojo Y
   hace `scrollIntoView`, así que allí el error va a buscar a la persona.

   ── LO QUE FALTABA DECIR, Y ES LO MÁS ÚTIL DE TODO ─────────────────────────
   Un error de escritura deja una pregunta que el mensaje no contestaba nunca:
   **¿lo repito o no?** Y las dos respuestas equivocadas cuestan dinero — no
   repetir lo que se perdió, o repetir lo que sí quedó y duplicarlo. La
   respuesta se deduce del status, y la diferencia importante es el gotcha 74
   de `CLAUDE.md`: cuando una Netlify Function muere a los 10 s montando un
   aviso con adjuntos, **el dato ya está escrito** y lo que se pierde es la
   RESPUESTA. Ahí «vuelva a intentarlo» es el peor consejo posible.
   ═══════════════════════════════════════════════════════════════════════════ */

export type EstadoDelDato = 'nada' | 'quizas' | 'nosesabe';

/** Qué hacer con lo que se estaba guardando. Se dice DESPUÉS del qué pasó. */
export const FRASES_DEL_DATO: Record<EstadoDelDato, string> = {
  nada: 'No se guardó nada: el portal rechazó el envío antes de registrarlo. Puede volver a intentarlo sin que quede duplicado.',
  quizas: 'OJO: puede que sí se haya guardado. El portal se cortó mientras lo registraba, así que antes de volver a intentarlo búsquelo en la sección — si lo repite y ya estaba, quedará dos veces.',
  nosesabe: 'No se puede saber si quedó guardado. Búsquelo en la sección antes de volver a intentarlo, para no cargarlo dos veces.',
};

/**
 * En qué quedó el dato que se estaba escribiendo, a partir del status.
 *
 * El reparto no es una precaución genérica, sale de dónde muere cada fallo:
 *
 * · **Nada** — el envío no llegó a la escritura. El 401 y el 403 los corta
 *   `requiereSesion()` en la primera línea del endpoint; el **413 lo devuelve
 *   la plataforma ANTES de que la función corra** (gotcha 76: el techo real es
 *   ~4,5 MB y ese 413 ni trae cuerpo); el 400 y el 422 son la validación, que
 *   va antes del INSERT; el 409 es un choque que se comprueba antes; el 429 no
 *   deja entrar; el 404 no encontró a qué escribir.
 * · **Quizás** — se cortó el reloj, no la lógica: 408, 502 y 504. Es el gotcha
 *   74, el fallo más caro de diagnosticar del portal, porque el dato está y la
 *   pantalla dice que no.
 * · **No se sabe** — un 5xx puede haber caído a mitad, y `fetch` lanzando
 *   (`status 0`) puede ser una petición que salió y cuya respuesta se perdió.
 *   La excepción es no tener red: si `navigator.onLine` dice que NO hay —y en
 *   ese sentido no miente—, la petición no salió del equipo y no se guardó nada.
 */
export function estadoDelDato(status: number, enLinea?: boolean | undefined): EstadoDelDato {
  if (status === 0) return enLinea === false ? 'nada' : 'nosesabe';
  if (status === 408 || status === 502 || status === 504) return 'quizas';
  if (status >= 500) return 'nosesabe';
  return 'nada';
}

/**
 * Los CAMPOS que el servidor rechazó, sacados del `detalle` de la respuesta,
 * para que el mensaje diga cuál revisar en vez de «datos inválidos» a secas.
 *
 * `detalle` viene de dos formas y las dos son legítimas: el array de `issues`
 * de zod —`detalle: p.error.issues`, que es lo que devuelven los 400 de los 62
 * endpoints— y un texto que el endpoint escribe a mano cuando sabe explicarlo
 * mejor (`cartera-carga.mts`: «se esperan DDH, DHV o DIFRANCA»).
 *
 * Del `issue` se usa el `path` y NO el `message`: los mensajes de zod están en
 * inglés («Required», «Expected number, received string») y en la pantalla de
 * un asesor no dicen nada. El `path` sí es el nombre del campo del formulario.
 */
export function camposDelDetalle(detalle: unknown): string | null {
  if (typeof detalle === 'string') return detalle.trim() || null;
  if (!Array.isArray(detalle)) return null;

  const campos: string[] = [];
  for (const it of detalle) {
    if (!it || typeof it !== 'object') continue;
    const ruta = (it as { path?: unknown }).path;
    if (!Array.isArray(ruta) || !ruta.length) continue;
    /* Los índices numéricos de un array se dicen en base 1, que es como los
     * cuenta la persona: `lineas.3.cantidad` → «línea 4 → cantidad». */
    const nombre = ruta
      .map((t) => (typeof t === 'number' ? `${t + 1}` : String(t)))
      .join(' → ');
    if (nombre && !campos.includes(nombre)) campos.push(nombre);
  }
  if (!campos.length) return null;

  /* Diez basta: una lista de cuarenta campos no se lee, y el resto se cuenta. */
  const cabe = campos.slice(0, 10);
  const resto = campos.length - cabe.length;
  return cabe.join(', ') + (resto > 0 ? ` y ${resto} más` : '');
}

/* ─────────────────────────────────────────────────────────────────────────────
   EL AVISO QUE VA A BUSCAR A LA PERSONA

   El mismo patrón que el gancho de caducidad de arriba, y por el mismo motivo:
   esto lo dispara código que está FUERA de React —el `onError` global de las
   mutaciones y el vigilante de promesas sin `catch`, los dos en `main.tsx`— y
   lo pinta un componente. `AvisoDeFalloGlobal` (`componentes/AvisoDeFallo.tsx`)
   se registra al montarse y es de UNO en uno, como el proveedor de sesión.

   NO se anuncian los fallos de SESIÓN: de esos ya se encarga el gancho de
   arriba, que devuelve al acceso con su mensaje. Sacar además un aviso flotante
   sería decir dos veces lo mismo en la única pantalla donde el portal ya es
   imposible de ignorar.
   ───────────────────────────────────────────────────────────────────────────── */

/** Lo que el aviso necesita saber para explicarse. */
export interface FalloAnunciado {
  /** El status HTTP; `0` cuando `fetch` lanzó. */
  status: number;
  /** El texto ya traducido: lo que lee la persona. */
  mensaje: string;
  /** El `codigo` de la respuesta, si trajo. */
  codigo?: string | null | undefined;
  /** El `detalle`, para nombrar los campos rechazados. */
  detalle?: unknown;
  /** `navigator.onLine` en el momento del fallo, si se sabe. */
  enLinea?: boolean | undefined;
  /**
   * Qué estaba haciendo la persona, en sus palabras («Guardar la planificación»).
   * Sin esto el aviso dice «la acción»; con esto dice cuál, y en una pantalla
   * con seis botones esa diferencia es todo.
   */
  accion?: string | undefined;
  /**
   * `true` solo cuando lo que falló ESCRIBÍA. Es lo que decide si el aviso
   * puede hablar de si el dato quedó o no, y no se adivina: lo pone el `onError`
   * global de las mutaciones (`main.tsx`), que sabe que una mutación escribe.
   *
   * El vigilante de promesas sin `catch` NO puede saberlo —un `ApiError` no
   * lleva el método— y por eso no lo pone. Y ahí callarse es lo correcto:
   * decirle «no se guardó nada, puede volver a intentarlo» a quien solo estaba
   * LEYENDO es una frase que no significa nada, y la que peor sienta es la otra
   * («puede que sí se haya guardado») sobre algo que nunca escribió.
   */
  escritura?: boolean | undefined;
}

type AlFallar = (f: FalloAnunciado) => void;
let alFallar: AlFallar | null = null;

/** `null` para quitarlo. Lo llama el componente del aviso al montarse. */
export function registrarAvisoDeFallo(fn: AlFallar | null): void {
  alFallar = fn;
}

/**
 * Anuncia un fallo de ESCRITURA para que salga en pantalla pase lo que pase,
 * incluso si quien lo pidió se tragó el error.
 *
 * Devuelve si lo anunció, que es lo que hace comprobable la regla de la sesión.
 */
export function anunciarFallo(f: FalloAnunciado): boolean {
  if (esFalloDeSesion(f.status, f.codigo)) return false;
  if (!alFallar) return false;
  alFallar(f);
  return true;
}

/**
 * El texto entero del aviso, en el orden que pidió la usuaria el 2026-08-31:
 * primero QUÉ pasó, después QUÉ hacer. Aquí se le añade el tercer renglón, que
 * es el que faltaba: en qué quedó lo que estaba guardando.
 *
 * Es una función y no está metida en el componente para poder comprobarla sin
 * montar React: es la frase que va a leer un asesor con el acta a medias.
 */
export function textoDelFallo(f: FalloAnunciado): {
  titulo: string; mensaje: string; campos: string | null; dato: string | null;
} {
  return {
    titulo: f.accion
      ? `No se pudo completar: ${f.accion}`
      : 'La acción no se completó',
    mensaje: f.mensaje,
    campos: camposDelDetalle(f.detalle),
    /* Solo si se sabe que escribía. Ver `escritura` en `FalloAnunciado`. */
    dato: f.escritura ? FRASES_DEL_DATO[estadoDelDato(f.status, f.enLinea)] : null,
  };
}
